import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import Grid from "@mui/material/Grid2";
import { TableEvaluation } from "./components";
import { Link } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { FC, useEffect } from "react";
import { RelatedCompetencySkills } from "./components/RelatedCompetencySkills";
import { RelevantResourcesLink } from "./components/RelevantResourcesLink";
import { initialValuesEPA, validationSchemaEPA } from "./constants";
import { useRequirementCreateAndEdit } from "./useRequirementCreateAndEdit";
import { REQUIREMENT } from "helpers/constants";
import { TagManager } from "components/TagManager";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { useGetRequirementsByTypeQuery } from "graphql/__generated__/hooks";
import { useMemo, useState } from "react";
import { Requirement } from "types/requirement";
import { EvaluationMetric } from "types/evaluationMetric";
import { Resource } from "types/resource";

type EPACreateAndEditProps = {
  edit?: boolean;
};

export const EPACreateAndEdit: FC<EPACreateAndEditProps> = ({
  edit = false,
}) => {
  const {
    competencySkills,
    setCompetencySkills,
    relevantResourceLinks,
    isOrgView,
    setRelevantResourceLinks,
    handleCreateRequirement,
    handleEditRequirement,
    dataRequirement,
    evaluationMetrics,
    setEvaluationMetrics,
    tags,
    handleAddTag,
    handleDeleteTag,
    customLabels,
    organizationName,
  } = useRequirementCreateAndEdit(REQUIREMENT.REQUIREMENT_EPA, edit);

  const [openImportRequirementModal, setOpenImportRequirementModal] =
    useState(false);

  const { data: requirementsData } = useGetRequirementsByTypeQuery({
    variables: { reqType: REQUIREMENT.REQUIREMENT_EPA },
  });

  const epaRequirements = useMemo(
    () => (requirementsData?.requirement as Requirement[]) || [],
    [requirementsData],
  );

  const titleSuffix =
    isOrgView && organizationName ? ` [${organizationName}]` : "";

  const formik = useFormik({
    initialValues: initialValuesEPA,
    validationSchema: validationSchemaEPA,
    onSubmit: (values) => {
      if (edit) {
        handleEditRequirement(values);
      } else {
        handleCreateRequirement(values);
      }
    },
  });

  // FIll Formik values with requirement data to Edit
  useEffect(() => {
    if (dataRequirement?.requirement_by_pk && edit) {
      const [hour, minutes] =
        dataRequirement.requirement_by_pk.task_time?.split(":") || [];

      formik.setValues({
        name: dataRequirement.requirement_by_pk.name || "",
        description: dataRequirement.requirement_by_pk.description ?? "",
        overview: dataRequirement.requirement_by_pk.overview ?? "",
        min_small_animals_count:
          dataRequirement.requirement_by_pk.min_small_animals_count ?? 0,
        min_large_animals_count:
          dataRequirement.requirement_by_pk.min_large_animals_count ?? 0,
        min_performance: dataRequirement.requirement_by_pk.min_performance ?? 0,
        hour: hour ? parseInt(hour) : 0,
        minutes: minutes ? parseInt(minutes) : 0,
        min_score: dataRequirement.requirement_by_pk.min_score ?? 0,
      });
    }
  }, [dataRequirement, edit]);

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} EPA${titleSuffix}`}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_EPAS
          : BREADCRUMB_PAGE.EPA_DETAIL
      }
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ maxWidth: 960 }}>
            {!edit && (
              <Box mb={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenImportRequirementModal(true)}
                >
                  Import from Existing EPA
                </Button>
              </Box>
            )}

            <Box mb={2}>
              <Typography variant="h5">Requirement Overview</Typography>
              <Divider />
            </Box>

            <Stack spacing={1}>
              <Box>
                <TextField
                  size="small"
                  id="name"
                  name="name"
                  label="Requirement Name"
                  fullWidth
                  margin="dense"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && !!formik.errors.name}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Box>

              <Box>
                <TextField
                  size="small"
                  id="overview"
                  name="overview"
                  label="Requirement Overview"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  value={formik.values.overview}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.overview && !!formik.errors.overview}
                  helperText={formik.touched.overview && formik.errors.overview}
                />
              </Box>
              <Box>
                <TextField
                  size="small"
                  id="description"
                  name="description"
                  label="Requirement Description"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={5}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description && !!formik.errors.description
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Box>
            </Stack>

            <Box my={2}>
              <Typography variant="h5">Goal Definition</Typography>
              <Divider />
            </Box>

            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={2}>
                    <Box>
                      <TextField
                        size="small"
                        id="min_small_animals_count"
                        name="min_small_animals_count"
                        label="Minimum Small Animal Count"
                        fullWidth
                        margin="dense"
                        type="number"
                        value={formik.values.min_small_animals_count}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.min_small_animals_count &&
                          !!formik.errors.min_small_animals_count
                        }
                        helperText={
                          formik.touched.min_small_animals_count &&
                          formik.errors.min_small_animals_count
                        }
                      />
                    </Box>
                    <Box>
                      <TextField
                        size="small"
                        id="min_large_animals_count"
                        name="min_large_animals_count"
                        label="Minimum Large Animal Count"
                        fullWidth
                        margin="dense"
                        type="number"
                        value={formik.values.min_large_animals_count}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.min_large_animals_count &&
                          !!formik.errors.min_large_animals_count
                        }
                        helperText={
                          formik.touched.min_large_animals_count &&
                          formik.errors.min_large_animals_count
                        }
                      />
                    </Box>
                    <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                      <Box sx={{ minWidth: 135, height: 3, lineHeight: 3 }}>
                        Allowed Task Time
                      </Box>
                      <Box sx={{ paddingTop: { xs: 4, sm: 0 } }}>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            size="small"
                            id="hour"
                            name="hour"
                            label="Hour"
                            margin="dense"
                            type="number"
                            value={formik.values.hour}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.hour && !!formik.errors.hour}
                            helperText={
                              formik.touched.hour && formik.errors.hour
                            }
                          />
                          <TextField
                            size="small"
                            id="minutes"
                            name="minutes"
                            label="Minutes"
                            margin="dense"
                            type="number"
                            value={formik.values.minutes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.minutes && !!formik.errors.minutes
                            }
                            helperText={
                              formik.touched.minutes && formik.errors.minutes
                            }
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={2}>
                    <Box>
                      <TextField
                        size="small"
                        id="min_performance"
                        name="min_performance"
                        label="Minimum Attempts"
                        fullWidth
                        margin="dense"
                        type="number"
                        value={formik.values.min_performance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.min_performance &&
                          !!formik.errors.min_performance
                        }
                        helperText={
                          formik.touched.min_performance &&
                          formik.errors.min_performance
                        }
                      />
                    </Box>
                    <Box>
                      <TextField
                        size="small"
                        id="min_score"
                        name="min_score"
                        label="Target Score On Rubric"
                        fullWidth
                        margin="dense"
                        type="number"
                        value={formik.values.min_score}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.min_score && !!formik.errors.min_score
                        }
                        helperText={
                          formik.touched.min_score && formik.errors.min_score
                        }
                      />
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box my={2}>
              <Typography variant="h5">Evaluation Metric</Typography>
              <Divider />
            </Box>

            <Box mt={2}>
              <TableEvaluation
                requirementType={REQUIREMENT.REQUIREMENT_EPA}
                evaluationMetrics={evaluationMetrics}
                setEvaluationMetrics={setEvaluationMetrics}
                customLabels={customLabels}
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Supplemental Information</Typography>
              <Divider />
            </Box>

            <Box>
              <RelevantResourcesLink
                relevantResourceLinks={relevantResourceLinks}
                onChange={(newRelevantResourceLinks) =>
                  setRelevantResourceLinks(newRelevantResourceLinks)
                }
              />
            </Box>

            <Box mt={2}>
              <RelatedCompetencySkills
                competencySkills={competencySkills}
                onChange={(newCompetencySkills) =>
                  setCompetencySkills(newCompetencySkills)
                }
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Tags</Typography>
              <Divider />
            </Box>

            <Box>
              <TagManager
                tags={tags}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                isEditing={true}
              />
            </Box>

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button variant="contained" type="submit">
                  Save Changes
                </Button>
                <Button variant="outlined" component={Link} to="/epas">
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Box>
        </form>
      </FormikProvider>

      {openImportRequirementModal && (
        <EntitySelectorWithTabs
          open={openImportRequirementModal}
          onClose={() => setOpenImportRequirementModal(false)}
          title="Import from Existing EPA"
          listEntities={epaRequirements}
          filterCriteria={(search: string, data: Requirement) =>
            data.name.toLowerCase().includes(search.toLowerCase())
          }
          onSelect={(selected: Requirement | null) => {
            if (!selected) return;

            const [hour, minutes] = selected.task_time?.split(":") || [];
            const importedName =
              organizationName && isOrgView
                ? `${selected.name} [${organizationName}]`
                : selected.name || "";

            formik.setValues({
              ...formik.values,
              name: importedName,
              description: selected.description ?? "",
              overview: selected.overview ?? "",
              min_small_animals_count:
                selected.min_small_animals_count ?? 0,
              min_large_animals_count:
                selected.min_large_animals_count ?? 0,
              min_performance: selected.min_performance ?? 0,
              hour: hour ? parseInt(hour, 10) : 0,
              minutes: minutes ? parseInt(minutes, 10) : 0,
              min_score: selected.min_score ?? 0,
            });

            setEvaluationMetrics(
              (selected.evaluation_metrics as EvaluationMetric[]) || [],
            );

            setRelevantResourceLinks(
              (selected.resources || []).map(
                (r: any) => r.resource as Resource,
              ),
            );

            setCompetencySkills(
              (selected.competencies || [])
                .map((c: any) => c.competency as Requirement)
                .filter(Boolean),
            );
          }}
          keyField="requirement_id"
          displayField="name"
          tabs={[
            {
              label: "EPA",
              filterTabCriteria: (requirement: Requirement) =>
                requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
            },
          ]}
        />
      )}
    </MainLayoutContainer>
  );
};
